import { useCallback, useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Vibe Board — Piece #4.
 * Live activity strip that auto-refreshes every 10s so Shannon can watch
 * applications, projects, payouts, emails, and COO decisions flow in real
 * time while she and Shaneka walk properties or test the dashboard.
 */
const VibeBoard = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [filter, setFilter] = useState("all");
  const [err, setErr] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [newSinceCollapse, setNewSinceCollapse] = useState(0);
  const lastSeenTsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/activity-stream?limit=40`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      const evs = d.events || [];
      setEvents(evs);
      setLastUpdated(new Date());
      setErr("");
      // Track new events that arrived while collapsed
      if (!expanded && evs.length > 0) {
        const newestTs = evs[0]?.ts;
        if (lastSeenTsRef.current && newestTs && newestTs > lastSeenTsRef.current) {
          const newCount = evs.filter((e) => e.ts > lastSeenTsRef.current).length;
          setNewSinceCollapse((c) => c + newCount);
          lastSeenTsRef.current = newestTs;
        } else if (!lastSeenTsRef.current) {
          lastSeenTsRef.current = newestTs;
        }
      } else if (expanded && evs.length > 0) {
        lastSeenTsRef.current = evs[0]?.ts;
        setNewSinceCollapse(0);
      }
    } catch (e) {
      setErr(e.message || "Could not load vibe board.");
    } finally {
      setLoading(false);
    }
  }, [expanded]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      if (next) {
        setNewSinceCollapse(0);
        lastSeenTsRef.current = events[0]?.ts || null;
      }
      return next;
    });
  };

  if (loading && events.length === 0) return null;
  if (err && events.length === 0) return null; // fail-quiet

  const shown = filter === "all" ? events : events.filter((e) => e.type === filter);
  const errorCount = events.filter((e) => e.severity === "error").length;
  const warnCount  = events.filter((e) => e.severity === "warn").length;
  const okCount    = events.filter((e) => e.severity === "success").length;

  return (
    <div data-testid="vibe-board" style={{
      background: "linear-gradient(135deg, rgba(90,200,216,0.08), rgba(184,145,90,0.06))",
      border: "1px solid rgba(90,200,216,0.4)",
      borderRadius: 8,
      padding: "0.9rem 1.1rem",
    }}>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.7rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
          <span data-testid="vibe-pulse" style={{
            width: 10, height: 10, borderRadius: "50%",
            background: "#5AC8D8",
            boxShadow: "0 0 10px #5AC8D8",
            animation: "vibePulse 1.6s ease-in-out infinite",
          }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: "#5AC8D8", fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 800 }}>
              Vibe Board · Live Activity {lastUpdated && <span style={{ color: "#9CA3AF", fontWeight: 600, letterSpacing: "0.1em" }}>· updated {lastUpdated.toLocaleTimeString()}</span>}
            </div>
            <div data-testid="vibe-headline" style={{ color: "#F3F4F6", fontSize: "0.92rem", fontWeight: 700, marginTop: 3 }}>
              {events.length} events · {" "}
              <span style={{ color: "#00DC78" }}>{okCount} ✓</span> · {" "}
              <span style={{ color: "#FFB347" }}>{warnCount} ⚠</span> · {" "}
              <span style={{ color: "#F87171" }}>{errorCount} ✕</span>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.35rem", alignItems: "center", flexWrap: "wrap" }}>
          {expanded && (
            <select
              data-testid="vibe-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                       border: "1px solid rgba(90,200,216,0.4)", borderRadius: 4,
                       padding: "0.3rem 0.55rem", fontSize: "0.75rem", fontWeight: 700 }}>
              <option value="all">All events</option>
              <option value="vendor_app">Vendor applications</option>
              <option value="vendor_prospect">Hunter prospects</option>
              <option value="project">Projects</option>
              <option value="payout">Payouts</option>
              <option value="email">Emails</option>
              <option value="coo">COO decisions</option>
              <option value="sms">SMS (Twilio)</option>
            </select>
          )}
          <button
            type="button"
            data-testid="vibe-toggle"
            onClick={toggle}
            style={{
              background: "transparent",
              border: "1px solid #5AC8D8",
              color: "#5AC8D8",
              padding: "0.35rem 0.85rem",
              borderRadius: 4,
              fontSize: "0.75rem",
              fontWeight: 800,
              cursor: "pointer",
              whiteSpace: "nowrap",
              position: "relative",
            }}
          >
            {expanded ? "Collapse" : "Expand"}
            {!expanded && newSinceCollapse > 0 && (
              <span data-testid="vibe-new-badge" style={{
                position: "absolute",
                top: -8,
                right: -8,
                background: "#00DC78",
                color: "#062617",
                borderRadius: "50%",
                width: 20, height: 20,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.65rem",
                fontWeight: 900,
              }}>{newSinceCollapse}</span>
            )}
          </button>
        </div>
      </div>

      {expanded && (
        <div data-testid="vibe-events" style={{ marginTop: "0.8rem", display: "grid", gap: "0.35rem", maxHeight: 340, overflowY: "auto" }}>
          {shown.length === 0 && (
            <div style={{ color: "#9CA3AF", fontSize: "0.82rem", padding: "0.7rem 0.3rem", fontStyle: "italic" }}>
              No activity yet in this filter. Kick off an application or create a project — events appear here in real time.
            </div>
          )}
          {shown.map((e, i) => {
            const sevColor = e.severity === "success" ? "#00DC78"
                           : e.severity === "warn"    ? "#FFB347"
                           : e.severity === "error"   ? "#F87171"
                           : "#5AC8D8";
            const typeIcon = {
              vendor_app: "🧾",
              vendor_prospect: "🎯",
              project: "🏠",
              payout: "💰",
              email: "📧",
              coo: "🤖",
              sms: "💬",
            }[e.type] || "•";
            return (
              <div key={`${e.type}-${e.ref_id || i}-${e.ts}`} data-testid={`vibe-event-${e.type}-${e.ref_id || i}`}
                   style={{ background: "rgba(0,0,0,0.22)", padding: "0.5rem 0.75rem",
                            borderRadius: 4, borderLeft: `3px solid ${sevColor}`,
                            display: "flex", gap: "0.6rem" }}>
                <div style={{ fontSize: "1.1rem", flexShrink: 0 }}>{typeIcon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
                    <div style={{ color: "#F3F4F6", fontSize: "0.85rem", fontWeight: 700 }}>{e.title}</div>
                    <div style={{ color: "#9CA3AF", fontSize: "0.72rem" }}>
                      {e.ts ? new Date(e.ts).toLocaleTimeString() : ""}
                    </div>
                  </div>
                  {e.detail && (
                    <div style={{ color: "#ADBED1", fontSize: "0.76rem", marginTop: 2 }}>{e.detail}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes vibePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.55; transform: scale(0.75); }
        }
      `}</style>
    </div>
  );
};

export default VibeBoard;
